import React, { useContext, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { 
    TextField, 
    Box, 
    Alert, 
    FormGroup, 
    FormControlLabel, 
    Checkbox, 
    Typography 
} from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';

import useApi from "../services/api";
import { GlobalContext } from "../contexts/global-context";
import { AuthContext } from "../contexts/auth-context";

export function SsoEmailCollection() {
    const { addUserEmail } = useApi();
    const { setAuthModalType, ssoUserData, setSsoUserData } = useContext(GlobalContext);
    const { setEmail } = useContext(AuthContext);
    const [generalError, setGeneralError] = useState({
        isErrorHidden: true,
        errorMessage: ""
    });

    const formik = useFormik({
        initialValues: {
            email: "",
            promo_consent: true,
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Must be a valid email address")
                .required("Required"),
            promo_consent: Yup.bool()
                .required()
                .oneOf([true, false])
        }),
        onSubmit: async (values) => {
            // Clear any previously rendered errors
            setGeneralError({
                isErrorHidden: true,
                errorMessage: ""
            });

            try {
                // Pass email data and session token if available
                const emailData = { 
                    email: values.email,
                    promo_consent: values.promo_consent
                };
                
                const response = await addUserEmail(emailData, ssoUserData?.sessionToken);
                
                // Check if the response was successful
                if (response.status === 200 || response.status === 201) {
                    // Store email for the emailed component to show confirmation message
                    setEmail(values.email);
                    // Clear SSO user data since we're done with it
                    setSsoUserData(null);
                    // Switch to emailed modal to show "check your email" message
                    setAuthModalType("emailed");
                }
            } catch (err) {
                const res = err?.response;
                if (res?.status === 400 && res?.data) {
                    // Handle specific error format: {"error": "Email address is already in use"}
                    if (res.data.error) {
                        if (res.data.error.includes("already in use")) {
                            // Show as field error for email
                            formik.setErrors({ email: res.data.error });
                        } else {
                            // Show as general error
                            setGeneralError({
                                isErrorHidden: false,
                                errorMessage: res.data.error
                            });
                        }
                    } else {
                        // Handle field validation errors format: {"field": ["error message"]}
                        let obj = {};
                        for (const k in res.data) {
                            if (Object.prototype.hasOwnProperty.call(formik.values, k)) {
                                obj[k] = Array.isArray(res.data[k]) ? res.data[k][0] : res.data[k];
                            } else {
                                const errorMsg = Array.isArray(res.data[k]) ? res.data[k].join(" ") : res.data[k];
                                setGeneralError({
                                    isErrorHidden: false,
                                    errorMessage: errorMsg
                                });
                            }
                        }
                        formik.setErrors(obj);
                    }
                } else {
                    // Generic network or unexpected error
                    setGeneralError({
                        isErrorHidden: false,
                        errorMessage: err.message || "Failed to add email. Please try again."
                    });
                }
            } finally {
                formik.setSubmitting(false);
            }
        }
    });

    const providerName = ssoUserData?.provider === 'facebook' ? 'Facebook' : 'Google';
    const username = ssoUserData?.user?.username || 'there';

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 0,
                m: 'auto'
            }}>
                {!generalError.isErrorHidden && (
                    <Alert sx={{mb: 2}} severity="error">{generalError.errorMessage}</Alert>
                )}
                
                <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                    Sorry <strong>{username}</strong>, we could not access your email address from {providerName}. 
                    Please enter it below so we can send you invoices and communications.
                </Typography>

                <TextField 
                    id="email" 
                    label="Email Address" 
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    disabled={formik.isSubmitting}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    autoComplete="email"
                />

                <FormGroup sx={{ mt: 1 }}>
                    <FormControlLabel 
                        control={
                            <Checkbox 
                                size="small"
                                id="promo_consent"
                                onChange={formik.handleChange}
                                checked={formik.values.promo_consent}
                                disabled={formik.isSubmitting}
                            />
                        }
                        label={
                            <Typography variant="body2" component="span">
                                Get updates & news from Plancrafted
                            </Typography>
                        }
                    />
                </FormGroup>

                <LoadingButton
                    sx={{mt: 2}} 
                    variant="contained"
                    fullWidth
                    loading={formik.isSubmitting}
                    type="submit"
                >
                    <span>Continue</span>
                </LoadingButton>
            </Box>
        </form>
    );
}